import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { getApiBase } from '../../lib/utils'

type ApiKeyItem = {
  id: number
  name: string
  key_prefix: string
  created_at: string
  last_used_at: string | null
  is_legacy: boolean
}

type ApiKeyListResponse = {
  items: ApiKeyItem[]
  total: number
  has_legacy_api_key: boolean
  has_any_api_key: boolean
}

type CreatedKey = {
  id: number
  name: string
  key_prefix: string
  api_key: string
}

export default function ApiKeyPage() {
  const [keys, setKeys] = useState<ApiKeyItem[]>([])
  const [loading, setLoading] = useState(true)
  const [newKeyName, setNewKeyName] = useState('')
  const [createdKey, setCreatedKey] = useState<CreatedKey | null>(null)
  const [creating, setCreating] = useState(false)
  const [revokingId, setRevokingId] = useState<number | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')
  const apiBase = useMemo(() => {
    if (typeof window === 'undefined') return getApiBase()
    return `${window.location.origin}${getApiBase()}`
  }, [])

  const loadApiKeys = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await api.get<ApiKeyListResponse>('/fapi/users/me/api-keys')
      setKeys(data.items ?? [])
    } catch (error) {
      console.error('Failed to load API keys', error)
      setKeys([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadApiKeys()
  }, [loadApiKeys])

  const handleCreate = useCallback(async () => {
    const name = newKeyName.trim()
    if (!name) {
      alert('Indiquez un nom pour la clé (ex. Site WordPress, WoW Booking)')
      return
    }

    try {
      setCreating(true)
      const { data } = await api.post<CreatedKey>('/fapi/users/me/api-keys', { name })
      setCreatedKey(data)
      setNewKeyName('')
      await loadApiKeys()
    } catch (error) {
      console.error('Failed to create API key', error)
      alert('Erreur lors de la création de la clé API')
    } finally {
      setCreating(false)
    }
  }, [loadApiKeys, newKeyName])

  const handleRevoke = useCallback(async (item: ApiKeyItem) => {
    const label = item.is_legacy ? 'la clé legacy' : `« ${item.name} »`
    if (!confirm(`Révoquer ${label} ? Les intégrations qui l'utilisent cesseront de fonctionner.`)) {
      return
    }

    try {
      setRevokingId(item.id)
      if (item.is_legacy) {
        await api.delete('/fapi/users/me/api-key')
      } else {
        await api.delete(`/fapi/users/me/api-keys/${item.id}`)
      }
      if (createdKey?.id === item.id) {
        setCreatedKey(null)
      }
      await loadApiKeys()
    } catch (error) {
      console.error('Failed to revoke API key', error)
      alert('Erreur lors de la révocation de la clé API')
    } finally {
      setRevokingId(null)
    }
  }, [createdKey, loadApiKeys])

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2500)
    } catch (error) {
      console.error('Clipboard error', error)
      setCopyState('error')
      setTimeout(() => setCopyState('idle'), 2500)
    }
  }, [])

  const formatDate = (value: string | null) => {
    if (!value) return '—'
    try {
      return new Date(value).toLocaleString()
    } catch {
      return value
    }
  }

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-sky-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.25),_transparent_68%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        <div className="mb-6">
          <Link
            to="/integrations"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour aux intégrations
          </Link>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-zinc-900 dark:text-white">Clés API Totemium</h1>
          <p className="mt-2 text-zinc-600 dark:text-zinc-400">
            Créez plusieurs clés nommées pour vos sites, clients ou intégrations (WordPress, WoW Booking, scripts…).
          </p>
        </div>

        <article className="relative overflow-hidden rounded-3xl border border-zinc-200/80 bg-white/95 p-6 shadow-md backdrop-blur dark:border-zinc-800/70 dark:bg-zinc-900/85">
          <div aria-hidden className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-sky-400/15 blur-3xl dark:bg-sky-500/20" />
          <div className="relative space-y-6">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">URL API Totemium</label>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100">
                  {apiBase}
                </code>
                <button
                  type="button"
                  onClick={() => handleCopy(apiBase)}
                  className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                >
                  Copier
                </button>
              </div>
            </div>

            {createdKey && (
              <div className="rounded-2xl border-2 border-emerald-500/30 bg-emerald-50/50 p-4 dark:border-emerald-500/20 dark:bg-emerald-900/10">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                  Clé « {createdKey.name} » créée
                </div>
                <p className="mb-3 text-xs text-emerald-600 dark:text-emerald-400">
                  Copiez cette clé maintenant. Elle ne sera plus affichée après fermeture de cette page.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 overflow-x-auto rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-emerald-700 dark:bg-zinc-800 dark:text-zinc-100">
                    {createdKey.api_key}
                  </code>
                  <button
                    type="button"
                    onClick={() => handleCopy(createdKey.api_key)}
                    className="rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600"
                  >
                    Copier
                  </button>
                </div>
                {copyState === 'copied' && (
                  <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-300">✓ Copié</p>
                )}
                <button
                  type="button"
                  onClick={() => setCreatedKey(null)}
                  className="mt-3 text-sm text-zinc-500 underline hover:text-zinc-700 dark:hover:text-zinc-300"
                >
                  Masquer
                </button>
              </div>
            )}

            <div>
              <label htmlFor="api-key-name" className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Nouvelle clé API
              </label>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                  id="api-key-name"
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Ex. Site WordPress, WoW Booking, Client ACME"
                  maxLength={120}
                  className="flex-1 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                />
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  className="rounded-full bg-sky-500 px-5 py-2 text-sm font-semibold text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {creating ? 'Création…' : 'Créer une clé'}
                </button>
              </div>
            </div>

            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Clés actives</h2>
              {loading ? (
                <p className="mt-3 text-sm text-zinc-500">Chargement…</p>
              ) : keys.length === 0 ? (
                <p className="mt-3 rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  Aucune clé API pour le moment. Créez-en une ci-dessus.
                </p>
              ) : (
                <ul className="mt-3 space-y-3">
                  {keys.map((item) => (
                    <li
                      key={`${item.is_legacy ? 'legacy' : item.id}`}
                      className="flex flex-col gap-3 rounded-2xl border border-zinc-200 bg-zinc-50/80 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-800/60 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-zinc-900 dark:text-zinc-100">{item.name}</span>
                          {item.is_legacy && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                              Legacy
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                          Préfixe : <code>{item.key_prefix}</code>
                          {' · '}
                          Créée : {formatDate(item.created_at)}
                          {item.last_used_at && (
                            <>
                              {' · '}
                              Dernière utilisation : {formatDate(item.last_used_at)}
                            </>
                          )}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRevoke(item)}
                        disabled={revokingId === item.id || (item.is_legacy && revokingId === 0)}
                        className="rounded-full border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-800 dark:bg-zinc-900 dark:text-rose-400 dark:hover:bg-rose-900/20"
                      >
                        {revokingId === item.id || (item.is_legacy && revokingId === 0) ? 'Révocation…' : 'Révoquer'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-sky-200/70 bg-sky-50/60 px-4 py-3 text-sm dark:border-sky-800 dark:bg-sky-900/20">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-sky-900 dark:text-sky-100">
                Comment utiliser vos clés API ?
              </h3>
              <ul className="space-y-2 text-xs text-sky-800 dark:text-sky-200 sm:text-sm">
                <li>• <strong>WordPress / WoW Booking :</strong> une clé dédiée par site ou intégration</li>
                <li>• <strong>Script embarqué :</strong> <code className="rounded bg-sky-100 px-1 dark:bg-sky-900">data-token="votre-clé"</code></li>
                <li>• <strong>API REST :</strong> header <code className="rounded bg-sky-100 px-1 dark:bg-sky-900">Authorization: Bearer votre-clé</code></li>
              </ul>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
